'use client';

import React from 'react';
import { Search, SlidersHorizontal, LayoutGrid, Map, ChevronDown } from 'lucide-react';
import type { FilterState } from './types';

interface Props {
  total: number;
  viewMode: 'grid' | 'map';
  setViewMode: (v: 'grid' | 'map') => void;
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  filtersOpen: boolean;
  setFiltersOpen: (v: boolean) => void;
}

const SORT_OPTIONS: { value: FilterState['sortBy']; label: string }[] = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'distance', label: 'Nearest first' },
  { value: 'newest', label: 'Newest' },
];

export default function DirectoryHeader({
  total, viewMode, setViewMode, filters, updateFilter, filtersOpen, setFiltersOpen
}: Props) {
  return (
    <div>
      {/* Page heading */}
      <div className="mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
          Local tradespeople near{' '}
          <span style={{ color: '#250C31' }}>{filters.postcode || 'you'}</span>
        </h1>
        <p className="text-gray-500 text-sm">
          {total.toLocaleString('en-GB')} verified providers within {filters.radius} miles
        </p>
      </div>

      {/* Search bar + controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filters.query}
            onChange={(e) => updateFilter('query', e.target.value)}
            placeholder="Search by name, trade, or specialism..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none transition-all"
            style={{ '--tw-ring-color': 'rgba(153,230,200,0.30)' } as React.CSSProperties}
          />
        </div>

        {/* Postcode */}
        <div className="relative">
          <input
            type="text"
            value={filters.postcode}
            onChange={(e) => updateFilter('postcode', e.target.value.toUpperCase())}
            placeholder="Postcode"
            className="w-full sm:w-32 px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none transition-all text-center font-medium"
          />
        </div>

        {/* Sort */}
        <div className="relative">
          <select
            value={filters.sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value as FilterState['sortBy'])}
            className="appearance-none pl-3 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none cursor-pointer text-gray-700"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Mobile filter toggle */}
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="lg:hidden flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <SlidersHorizontal size={15} />
          Filters
        </button>

        {/* View toggle */}
        <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-white">
          <button
            onClick={() => setViewMode('grid')}
            className="flex items-center gap-1.5 px-3 py-2.5 text-sm transition-colors"
            style={viewMode === 'grid' ? { backgroundColor: '#250C31', color: '#99E6C8' } : { color: '#6B7280' }}
            aria-label="Grid view"
          >
            <LayoutGrid size={15} />
            <span className="hidden sm:inline">Grid</span>
          </button>
          <button
            onClick={() => setViewMode('map')}
            className="flex items-center gap-1.5 px-3 py-2.5 text-sm transition-colors"
            style={viewMode === 'map' ? { backgroundColor: '#250C31', color: '#99E6C8' } : { color: '#6B7280' }}
            aria-label="Map view"
          >
            <Map size={15} />
            <span className="hidden sm:inline">Map</span>
          </button>
        </div>
      </div>
    </div>
  );
}